import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  RefreshControl,
  Alert,
  Modal,
  ActivityIndicator,
  Dimensions,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useDatabase } from '../../../context/DatabaseContext';
import { 
  useDashboard, 
  useTasks, 
  useProjects, 
  useTeamMembers,
  useTimeEntries,
  useNotifications,
  useRealTimeUpdates,
  useReports 
} from '../../../hooks/useDatabaseHooks';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width } = Dimensions.get('window');

/**
 * Database-Connected Manager Dashboard
 * Comprehensive management interface with team oversight, project management, and analytics
 */
const ManagerDashboard = ({ navigation }) => {
  // Database context and hooks
  const { 
    currentUser, 
    isOnline, 
    isSyncing,
    performSync,
    getSyncStatus 
  } = useDatabase();

  const { 
    dashboardStats, 
    loading: dashboardLoading, 
    refreshDashboard 
  } = useDashboard(currentUser?.id);

  const { 
    tasks, 
    loading: tasksLoading, 
    getTasksByStatus,
    getOverdueTasks,
    assignTask,
    updateTaskStatus 
  } = useTasks(currentUser?.company_id);

  const { 
    projects, 
    loading: projectsLoading,
    createProject,
    updateProject 
  } = useProjects(currentUser?.company_id);

  const { 
    teamMembers, 
    loading: teamLoading,
    getTeamPerformance 
  } = useTeamMembers(currentUser?.company_id);

  const { 
    timeEntries, 
    getTotalHours,
    getTeamHours 
  } = useTimeEntries(currentUser?.company_id, new Date().toISOString().split('T')[0]);

  const { 
    notifications, 
    getUnreadCount,
    markAsRead 
  } = useNotifications(currentUser?.id);

  const { 
    reports,
    generateReport 
  } = useReports(currentUser?.company_id);

  const { 
    isRefreshing, 
    manualRefresh, 
    syncStatus 
  } = useRealTimeUpdates(currentUser?.id, {
    interval: 30000,
    autoRefresh: true,
    syncOnMount: true
  });

  // Local state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedView, setSelectedView] = useState('overview'); // overview, projects, team, reports
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showAssignTask, setShowAssignTask] = useState(false);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  /**
   * Handle task assignment
   */
  const handleAssignTask = async (taskId, assigneeId) => {
    try {
      const result = await assignTask(taskId, assigneeId);
      
      if (result.success) {
        Alert.alert('Success', 'Task assigned successfully');
        setShowAssignTask(false);
      } else {
        Alert.alert('Error', result.error || 'Failed to assign task');
      }
    } catch (error) {
      console.error('Task assignment error:', error);
      Alert.alert('Error', 'An error occurred while assigning task');
    }
  };

  /**
   * Handle project creation
   */
  const handleCreateProject = async (projectData) => {
    try {
      const result = await createProject({
        ...projectData,
        manager_id: currentUser.id,
        company_id: currentUser.company_id,
        status: 'planning',
      });
      
      if (result.success) {
        Alert.alert('Success', 'Project created successfully');
        setShowCreateProject(false);
      } else {
        Alert.alert('Error', result.error || 'Failed to create project');
      }
    } catch (error) {
      console.error('Project creation error:', error);
      Alert.alert('Error', 'An error occurred while creating project');
    }
  };

  /**
   * Handle notification tap
   */
  const handleNotificationTap = async (notification) => {
    try {
      await markAsRead(notification.id);
      
      // Handle different notification types
      if (notification.type === 'task' && notification.data) {
        const data = JSON.parse(notification.data);
        if (data.task_id) {
          navigation.navigate('TaskDetails', { taskId: data.task_id });
        }
      } else if (notification.type === 'project' && notification.data) {
        const data = JSON.parse(notification.data);
        if (data.project_id) {
          navigation.navigate('ProjectDetails', { projectId: data.project_id });
        }
      }
      
      setShowNotifications(false);
    } catch (error) {
      console.error('Notification tap error:', error);
    }
  };

  /**
   * Generate performance report
   */
  const handleGenerateReport = async (reportType) => {
    try {
      const result = await generateReport({
        type: reportType,
        company_id: currentUser.company_id,
        date_range: {
          start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
          end: new Date().toISOString(),
        },
      });
      
      if (result.success) {
        navigation.navigate('ReportViewer', { report: result.data });
      } else {
        Alert.alert('Error', result.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Report generation error:', error);
      Alert.alert('Error', 'An error occurred while generating report');
    }
  };

  /**
   * Format time display
   */
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  /**
   * Format date display
   */
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#F44336';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  /**
   * Get status color
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'in_progress': return '#2196F3';
      case 'pending': return '#FF9800';
      case 'on_hold': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  /**
   * Render overview section
   */
  const renderOverview = () => (
    <View style={styles.sectionContainer}>
      {/* Dashboard Stats */}
      <View style={styles.statsContainer}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Ionicons name="briefcase" size={24} color="#2196F3" />
            <Text style={styles.statNumber}>{dashboardStats.activeProjects}</Text>
            <Text style={styles.statLabel}>Active Projects</Text>
          </View>
          
          <View style={styles.statCard}>
            <Ionicons name="people" size={24} color="#4CAF50" />
            <Text style={styles.statNumber}>{dashboardStats.teamMembers}</Text>
            <Text style={styles.statLabel}>Team Members</Text>
          </View>
          
          <View style={styles.statCard}>
            <Ionicons name="checkmark-circle" size={24} color="#FF9800" />
            <Text style={styles.statNumber}>{dashboardStats.pendingTasks}</Text>
            <Text style={styles.statLabel}>Pending Tasks</Text>
          </View>
          
          <View style={styles.statCard}>
            <Ionicons name="time" size={24} color="#9C27B0" />
            <Text style={styles.statNumber}>{dashboardStats.totalHours}h</Text>
            <Text style={styles.statLabel}>This Week</Text>
          </View>
        </View>
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsContainer}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity
            style={styles.quickActionItem}
            onPress={() => setShowCreateProject(true)}
          >
            <Ionicons name="add-circle" size={24} color="#2196F3" />
            <Text style={styles.quickActionText}>New Project</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionItem}
            onPress={() => setShowAssignTask(true)}
          >
            <Ionicons name="person-add" size={24} color="#4CAF50" />
            <Text style={styles.quickActionText}>Assign Task</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionItem}
            onPress={() => navigation.navigate('TeamManagement')}
          >
            <Ionicons name="people-circle" size={24} color="#FF9800" />
            <Text style={styles.quickActionText}>Manage Team</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionItem}
            onPress={() => handleGenerateReport('performance')}
          >
            <Ionicons name="bar-chart" size={24} color="#9C27B0" />
            <Text style={styles.quickActionText}>Reports</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recent Projects */}
      <View style={styles.recentContainer}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Recent Projects</Text>
          <TouchableOpacity onPress={() => setSelectedView('projects')}>
            <Text style={styles.seeAllText}>See All</Text>
          </TouchableOpacity>
        </View>
        
        {projects.slice(0, 3).map((project) => (
          <TouchableOpacity
            key={project.id}
            style={styles.projectCard}
            onPress={() => setSelectedProject(project)}
          >
            <View style={styles.projectHeader}>
              <Text style={styles.projectTitle} numberOfLines={1}>
                {project.name}
              </Text>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(project.status) }]}>
                <Text style={styles.statusBadgeText}>{project.status}</Text>
              </View>
            </View>
            
            <Text style={styles.projectDescription} numberOfLines={2}>
              {project.description || 'No description available'}
            </Text>
            
            <View style={styles.projectProgress}>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    { width: `${project.progress || 0}%` }
                  ]}
                />
              </View>
              <Text style={styles.progressText}>{project.progress || 0}%</Text>
            </View>
            
            <View style={styles.projectMeta}>
              <View style={styles.projectTeam}>
                <Ionicons name="people" size={14} color="#666" />
                <Text style={styles.projectTeamText}>
                  {project.team_count || 0} members
                </Text>
              </View>
              
              {project.deadline && (
                <Text style={styles.projectDeadline}>
                  Due: {new Date(project.deadline).toLocaleDateString()}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Team Performance */}
      <View style={styles.teamPerformanceContainer}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Team Performance</Text>
          <TouchableOpacity onPress={() => setSelectedView('team')}>
            <Text style={styles.seeAllText}>View Details</Text>
          </TouchableOpacity>
        </View>
        
        {teamMembers.slice(0, 5).map((member) => (
          <TouchableOpacity
            key={member.id}
            style={styles.teamMemberCard}
            onPress={() => setSelectedTeamMember(member)}
          >
            <View style={styles.memberAvatar}>
              <Text style={styles.memberAvatarText}>
                {member.name?.charAt(0) || member.email?.charAt(0) || 'U'}
              </Text>
            </View>
            
            <View style={styles.memberInfo}>
              <Text style={styles.memberName}>{member.name || member.email}</Text>
              <Text style={styles.memberRole}>{member.role_name || 'Employee'}</Text>
            </View>
            
            <View style={styles.memberStats}>
              <View style={styles.memberStat}>
                <Text style={styles.memberStatNumber}>{member.active_tasks || 0}</Text>
                <Text style={styles.memberStatLabel}>Tasks</Text>
              </View>
              
              <View style={styles.memberStat}>
                <Text style={styles.memberStatNumber}>{member.hours_this_week || 0}h</Text>
                <Text style={styles.memberStatLabel}>Hours</Text>
              </View>
            </View>
            
            <View style={[styles.performanceIndicator, {
              backgroundColor: member.performance_score >= 80 ? '#4CAF50' : 
                             member.performance_score >= 60 ? '#FF9800' : '#F44336'
            }]} />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  /**
   * Render projects section
   */
  const renderProjects = () => (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>All Projects</Text>
        <TouchableOpacity onPress={() => setShowCreateProject(true)}>
          <Ionicons name="add-circle" size={24} color="#2196F3" />
        </TouchableOpacity>
      </View>
      
      <FlatList
        data={projects}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item: project }) => (
          <TouchableOpacity
            style={styles.projectCard}
            onPress={() => navigation.navigate('ProjectDetails', { projectId: project.id })}
          >
            <View style={styles.projectHeader}>
              <Text style={styles.projectTitle}>{project.name}</Text>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(project.status) }]}>
                <Text style={styles.statusBadgeText}>{project.status}</Text>
              </View>
            </View>
            
            <Text style={styles.projectDescription} numberOfLines={2}>
              {project.description || 'No description available'}
            </Text>
            
            <View style={styles.projectProgress}>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    { width: `${project.progress || 0}%` }
                  ]}
                />
              </View>
              <Text style={styles.progressText}>{project.progress || 0}%</Text>
            </View>
            
            <View style={styles.projectMeta}>
              <Text style={styles.projectBudget}>
                Budget: ${project.budget?.toLocaleString() || 'N/A'}
              </Text>
              
              {project.deadline && (
                <Text style={styles.projectDeadline}>
                  Due: {new Date(project.deadline).toLocaleDateString()}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        )}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );

  /**
   * Render team section
   */
  const renderTeam = () => (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Team Management</Text>
        <TouchableOpacity onPress={() => navigation.navigate('AddTeamMember')}>
          <Ionicons name="person-add" size={24} color="#2196F3" />
        </TouchableOpacity>
      </View>
      
      <FlatList
        data={teamMembers}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item: member }) => (
          <TouchableOpacity
            style={styles.teamMemberCard}
            onPress={() => navigation.navigate('TeamMemberDetails', { memberId: member.id })}
          >
            <View style={styles.memberAvatar}>
              <Text style={styles.memberAvatarText}>
                {member.name?.charAt(0) || member.email?.charAt(0) || 'U'}
              </Text>
            </View>
            
            <View style={styles.memberInfo}>
              <Text style={styles.memberName}>{member.name || member.email}</Text>
              <Text style={styles.memberRole}>{member.role_name || 'Employee'}</Text>
              <Text style={styles.memberEmail}>{member.email}</Text>
            </View>
            
            <View style={styles.memberStats}>
              <View style={styles.memberStat}>
                <Text style={styles.memberStatNumber}>{member.active_tasks || 0}</Text>
                <Text style={styles.memberStatLabel}>Active Tasks</Text>
              </View>
              
              <View style={styles.memberStat}>
                <Text style={styles.memberStatNumber}>{member.completed_tasks || 0}</Text>
                <Text style={styles.memberStatLabel}>Completed</Text>
              </View>
              
              <View style={styles.memberStat}>
                <Text style={styles.memberStatNumber}>{member.hours_this_week || 0}h</Text>
                <Text style={styles.memberStatLabel}>This Week</Text>
              </View>
            </View>
            
            <View style={styles.memberActions}>
              <TouchableOpacity
                style={styles.memberActionButton}
                onPress={() => {
                  setSelectedTeamMember(member);
                  setShowAssignTask(true);
                }}
              >
                <Ionicons name="add-circle-outline" size={20} color="#2196F3" />
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.memberActionButton}
                onPress={() => navigation.navigate('TeamMemberPerformance', { memberId: member.id })}
              >
                <Ionicons name="bar-chart-outline" size={20} color="#4CAF50" />
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        )}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );

  /**
   * Render reports section
   */
  const renderReports = () => (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionTitle}>Reports & Analytics</Text>
      
      <View style={styles.reportsGrid}>
        <TouchableOpacity
          style={styles.reportCard}
          onPress={() => handleGenerateReport('project_performance')}
        >
          <Ionicons name="bar-chart" size={32} color="#2196F3" />
          <Text style={styles.reportTitle}>Project Performance</Text>
          <Text style={styles.reportDescription}>
            Detailed analysis of project progress and completion rates
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.reportCard}
          onPress={() => handleGenerateReport('team_productivity')}
        >
          <Ionicons name="people" size={32} color="#4CAF50" />
          <Text style={styles.reportTitle}>Team Productivity</Text>
          <Text style={styles.reportDescription}>
            Individual and team performance metrics and insights
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.reportCard}
          onPress={() => handleGenerateReport('time_tracking')}
        >
          <Ionicons name="time" size={32} color="#FF9800" />
          <Text style={styles.reportTitle}>Time Tracking</Text>
          <Text style={styles.reportDescription}>
            Hours worked, overtime, and time allocation analysis
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.reportCard}
          onPress={() => handleGenerateReport('financial')}
        >
          <Ionicons name="cash" size={32} color="#9C27B0" />
          <Text style={styles.reportTitle}>Financial Report</Text>
          <Text style={styles.reportDescription}>
            Budget utilization, costs, and financial projections
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Loading state
  if (dashboardLoading || tasksLoading || projectsLoading || teamLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#2196F3" />
          <Text style={styles.loadingText}>Loading manager dashboard...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={manualRefresh}
            colors={['#2196F3']}
            tintColor="#2196F3"
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <LinearGradient
          colors={['#1976D2', '#2196F3']}
          style={styles.header}
        >
          <View style={styles.headerTop}>
            <View style={styles.userInfo}>
              <TouchableOpacity
                style={styles.avatar}
                onPress={() => setShowProfile(true)}
              >
                <Text style={styles.avatarText}>
                  {currentUser?.name?.charAt(0) || currentUser?.email?.charAt(0) || 'M'}
                </Text>
              </TouchableOpacity>
              <View style={styles.userDetails}>
                <Text style={styles.userName}>
                  {currentUser?.name || currentUser?.full_name || 'Manager'}
                </Text>
                <Text style={styles.userRole}>
                  {currentUser?.role?.name || 'Manager'}
                </Text>
                <Text style={styles.userCompany}>
                  {currentUser?.company?.name || 'Company'}
                </Text>
              </View>
            </View>
            
            <View style={styles.headerActions}>
              {/* Sync Status */}
              <TouchableOpacity
                style={styles.syncButton}
                onPress={performSync}
                disabled={isSyncing}
              >
                <Ionicons
                  name={isSyncing ? "sync" : isOnline ? "cloud-done" : "cloud-offline"}
                  size={20}
                  color="#FFFFFF"
                  style={isSyncing ? styles.spinning : null}
                />
              </TouchableOpacity>
              
              {/* Notifications */}
              <TouchableOpacity
                style={styles.notificationButton}
                onPress={() => setShowNotifications(true)}
              >
                <Ionicons name="notifications" size={20} color="#FFFFFF" />
                {getUnreadCount() > 0 && (
                  <View style={styles.notificationBadge}>
                    <Text style={styles.notificationBadgeText}>
                      {getUnreadCount() > 99 ? '99+' : getUnreadCount()}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>
          </View>

          {/* Time and Date */}
          <View style={styles.timeContainer}>
            <Text style={styles.currentTime}>{formatTime(currentTime)}</Text>
            <Text style={styles.currentDate}>{formatDate(currentTime)}</Text>
          </View>

          {/* View Selector */}
          <View style={styles.viewSelector}>
            {[
              { key: 'overview', label: 'Overview', icon: 'grid' },
              { key: 'projects', label: 'Projects', icon: 'briefcase' },
              { key: 'team', label: 'Team', icon: 'people' },
              { key: 'reports', label: 'Reports', icon: 'bar-chart' },
            ].map((view) => (
              <TouchableOpacity
                key={view.key}
                style={[
                  styles.viewSelectorItem,
                  selectedView === view.key && styles.viewSelectorItemActive
                ]}
                onPress={() => setSelectedView(view.key)}
              >
                <Ionicons
                  name={view.icon}
                  size={16}
                  color={selectedView === view.key ? '#2196F3' : '#FFFFFF'}
                />
                <Text style={[
                  styles.viewSelectorText,
                  selectedView === view.key && styles.viewSelectorTextActive
                ]}>
                  {view.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </LinearGradient>

        {/* Content */}
        {selectedView === 'overview' && renderOverview()}
        {selectedView === 'projects' && renderProjects()}
        {selectedView === 'team' && renderTeam()}
        {selectedView === 'reports' && renderReports()}
      </ScrollView>

      {/* Modals and other components would go here */}
      {/* Similar to EmployeeDashboard modals but with manager-specific content */}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  header: {
    padding: 20,
    paddingTop: 40,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  userRole: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    marginBottom: 2,
  },
  userCompany: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  syncButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  notificationButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#F44336',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
  },
  timeContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  currentTime: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  currentDate: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
  },
  viewSelector: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 25,
    padding: 4,
  },
  viewSelectorItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  viewSelectorItemActive: {
    backgroundColor: '#FFFFFF',
  },
  viewSelectorText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  viewSelectorTextActive: {
    color: '#2196F3',
    fontWeight: '600',
  },
  sectionContainer: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  seeAllText: {
    color: '#2196F3',
    fontSize: 14,
    fontWeight: '500',
  },
  statsContainer: {
    marginBottom: 20,
    marginTop: -10,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 15,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  quickActionsContainer: {
    marginBottom: 20,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickActionItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 20,
    alignItems: 'center',
    width: (width - 60) / 2,
    marginBottom: 15,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  quickActionText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
    marginTop: 8,
    textAlign: 'center',
  },
  recentContainer: {
    marginBottom: 20,
  },
  projectCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 15,
    marginBottom: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
    marginRight: 10,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  projectDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
    lineHeight: 20,
  },
  projectProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#E0E0E0',
    borderRadius: 3,
    marginRight: 10,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#2196F3',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  projectMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  projectTeam: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  projectTeamText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 4,
  },
  projectBudget: {
    fontSize: 12,
    color: '#4CAF50',
    fontWeight: '500',
  },
  projectDeadline: {
    fontSize: 12,
    color: '#FF9800',
    fontWeight: '500',
  },
  teamPerformanceContainer: {
    marginBottom: 20,
  },
  teamMemberCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#2196F3',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  memberAvatarText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  memberInfo: {
    flex: 1,
    marginRight: 10,
  },
  memberName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  memberRole: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  memberEmail: {
    fontSize: 10,
    color: '#999',
  },
  memberStats: {
    flexDirection: 'row',
    marginRight: 10,
  },
  memberStat: {
    alignItems: 'center',
    marginHorizontal: 8,
  },
  memberStatNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  memberStatLabel: {
    fontSize: 10,
    color: '#666',
  },
  memberActions: {
    flexDirection: 'row',
  },
  memberActionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 5,
  },
  performanceIndicator: {
    width: 4,
    height: 40,
    borderRadius: 2,
    marginLeft: 10,
  },
  reportsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  reportCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    padding: 20,
    alignItems: 'center',
    width: (width - 60) / 2,
    marginBottom: 15,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  reportTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 5,
  },
  reportDescription: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    lineHeight: 16,
  },
});

export default ManagerDashboard;
